import { when } from 'mobx'
import type { IParamsSendFrom } from 'entities/SendFrom'

// Minimal structural view of the broadcast store the sender action needs. Keeps
// the action unit-testable with a fake and decoupled from the full view store.
export interface SenderTarget {
  loading: boolean
  sendFromStore: {
    loading: boolean
    setSendFrom: (params: IParamsSendFrom) => Promise<void>
    paramsSendFrom: IParamsSendFrom | null
  }
}

export type ApplySenderResult = { ok: true; number_id: number } | { ok: false; reason: string }

/**
 * Set the broadcast form's sending number and confirm it actually applied.
 *
 * The sending number lives in a sub-store (`sendFromStore`) whose numbers list
 * loads on its own lifecycle, separate from the broadcast store's `loading`.
 * If we select before that list is ready, `setSelectItem` silently no-ops and
 * the form shows no number. So we wait for BOTH stores to be ready, then verify
 * the selection took — returning a failure the caller can surface instead of a
 * false success.
 */
export const applyBroadcastSender = async (
  store: SenderTarget,
  number_id: number,
  timeout: number
): Promise<ApplySenderResult> => {
  await when(() => !store.loading, { timeout })
  await when(() => !store.sendFromStore.loading, { timeout })

  await store.sendFromStore.setSendFrom({ type: 'from_number', options: { number_id } })

  const applied = store.sendFromStore.paramsSendFrom?.options?.number_id
  if (applied !== number_id) {
    return {
      ok: false,
      reason:
        'The sending number could not be applied to the broadcast form. It may not be available for this broadcast type or inbox.',
    }
  }

  return { ok: true, number_id }
}
